import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { trpc } from '@org/clients/school';
import { Badge, StarRatingDisplay, type ChartSeries, type TimePoint } from '@org/ui';
import { useAnalytics } from '../analytics-context';
import { ReportCard } from '../report-card';
import { ReportTable } from '../report-table';
import { TimeSeriesCard } from '../time-series-card';
import { formatBucket, formatDate, formatRate } from '../format';

export function FeedbackReports() {
  const { t, i18n } = useTranslation('admin');
  const { input, compareInput, grain } = useAnalytics();
  const comparing = Boolean(compareInput);
  const navigate = useNavigate();
  const campaigns = trpc.analytics.feedback.campaigns.useQuery(input);
  const trend = trpc.analytics.feedback.ratingTrend.useQuery(input);
  const reportsOverTime = trpc.analytics.feedback.studentReportsOverTime.useQuery(input);
  const cmpReportsOverTime = trpc.analytics.feedback.studentReportsOverTime.useQuery(compareInput ?? input, { enabled: comparing });
  const cmpTrend = trpc.analytics.feedback.ratingTrend.useQuery(compareInput ?? input, { enabled: comparing });
  const reportsByTeacher = trpc.analytics.feedback.studentReportsByTeacher.useQuery(input);

  const lang = i18n.language;
  const xLabel = (b: string) => formatBucket(b, grain, lang);
  const trendSeries: ChartSeries[] = [{ key: 'avgRating', label: t('analytics.feedback.trend.series') }];
  const toTrend = (rows: { bucket: string; avgRating: number | null }[] | undefined): TimePoint[] =>
    (rows ?? []).map((p) => {
      const point: TimePoint = { bucket: p.bucket };
      if (p.avgRating !== null) point.avgRating = p.avgRating;
      return point;
    });
  const trendData = toTrend(trend.data);
  const cmpTrendData = toTrend(cmpTrend.data);
  const reportSeries: ChartSeries[] = [
    { key: 'midterm', label: t('analytics.feedback.reports.series.midterm') },
    { key: 'final', label: t('analytics.feedback.reports.series.final') },
    { key: 'drafts', label: t('analytics.feedback.reports.series.drafts') },
  ];

  return (
    <div className="space-y-6">
      <ReportCard
        title={t('analytics.feedback.campaigns.title')}
        description={t('analytics.feedback.campaigns.description')}
        isLoading={campaigns.isLoading}
        error={campaigns.error}
        isEmpty={campaigns.data?.length === 0}
      >
        <ReportTable
          rows={campaigns.data ?? []}
          rowKey={(r) => r.campaignId}
          defaultSort="created"
          onRowClick={(r) => navigate({ to: '/feedback/$campaignId', params: { campaignId: r.campaignId } })}
          columns={[
            { key: 'title', label: t('analytics.common.title'), value: (r) => r.title, className: 'font-medium' },
            {
              key: 'status',
              label: t('analytics.common.status'),
              value: (r) => r.status,
              render: (r) => <Badge variant={r.status === 'active' ? 'default' : 'outline'}>{t(`analytics.feedback.statuses.${r.status}`, { defaultValue: r.status })}</Badge>,
            },
            { key: 'type', label: t('analytics.common.type'), value: (r) => r.campaignType, render: (r) => t(`analytics.feedback.types.${r.campaignType}`, { defaultValue: r.campaignType }) },
            { key: 'created', label: t('analytics.feedback.campaigns.createdAt'), value: (r) => r.createdAt, render: (r) => formatDate(r.createdAt, lang) },
            { key: 'questions', label: t('analytics.feedback.campaigns.questions'), value: (r) => r.questions, align: 'right' },
            { key: 'submitted', label: t('analytics.feedback.campaigns.submitted'), value: (r) => r.submitted, align: 'right' },
            { key: 'dismissed', label: t('analytics.feedback.campaigns.dismissed'), value: (r) => r.dismissed, align: 'right' },
            {
              key: 'rating',
              label: t('analytics.feedback.campaigns.avgRating'),
              value: (r) => r.avgRating,
              render: (r) => (r.avgRating === null ? '—' : <StarRatingDisplay value={r.avgRating} size={14} />),
            },
          ]}
        />
        <p className="mt-2 text-xs text-muted-foreground">{t('analytics.feedback.campaigns.hint')}</p>
      </ReportCard>

      <div className="grid gap-6 lg:grid-cols-2">
        <TimeSeriesCard
          title={t('analytics.feedback.trend.title')}
          description={t('analytics.feedback.trend.description')}
          data={trendData}
          cmpData={comparing ? cmpTrendData : undefined}
          series={trendSeries}
          kind="line"
          yDomain={[0, 5]}
          valueFormat={(n) => n.toFixed(2)}
          isLoading={trend.isLoading}
          error={trend.error}
        />

        <TimeSeriesCard
          title={t('analytics.feedback.reports.title')}
          description={t('analytics.feedback.reports.description')}
          data={reportsOverTime.data}
          cmpData={cmpReportsOverTime.data}
          series={reportSeries}
          stacked
          isLoading={reportsOverTime.isLoading}
          error={reportsOverTime.error}
        />
      </div>

      <ReportCard
        title={t('analytics.feedback.byTeacher.title')}
        description={t('analytics.feedback.byTeacher.description')}
        isLoading={reportsByTeacher.isLoading}
        error={reportsByTeacher.error}
        isEmpty={reportsByTeacher.data?.length === 0}
      >
        <ReportTable
          rows={reportsByTeacher.data ?? []}
          rowKey={(r) => r.teacherId}
          defaultSort="published"
          columns={[
            { key: 'name', label: t('analytics.common.teacher'), value: (r) => r.name },
            { key: 'students', label: t('analytics.feedback.byTeacher.students'), value: (r) => r.students, align: 'right' },
            { key: 'created', label: t('analytics.feedback.byTeacher.created'), value: (r) => r.created, align: 'right' },
            { key: 'published', label: t('analytics.feedback.byTeacher.published'), value: (r) => r.published, align: 'right' },
            { key: 'rate', label: t('analytics.feedback.byTeacher.publishRate'), value: (r) => r.publishRate, render: (r) => formatRate(r.publishRate), align: 'right' },
            { key: 'days', label: t('analytics.feedback.byTeacher.avgDays'), value: (r) => r.avgDaysToPublish, render: (r) => r.avgDaysToPublish ?? '—', align: 'right' },
          ]}
        />
      </ReportCard>
    </div>
  );
}
